// @ts-nocheck
import { isEmpty, isEqual } from 'lodash';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useReportingMap } from 'sonata-platform-components';
import { CartographySettings, Legend } from 'sonata-platform-components/lib/types/types/reportingMap.types';
import { CustomThemeProvider, Loader, usePrevious } from 'sonata-react-components';
import { isDigitalCampaign, isDOOHCampaign } from 'sonata-static-storage/lib/helpers/campaign.helpers';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';

import { Map as MapComponent } from '@/components/Map';
import { getMetricsKeys, STORE_VISITS } from '@/helpers/reporting.helpers';
import { reportingFetchCampaignScreens, reportingSetCartographyMapConfig } from '@/store/actions/reporting.actions';
import {
  getCountriesWithEvents,
  getDefaultMapBoundingBox,
  getMapEvents,
  getMapLoading,
  getMetricsAndKpisByType,
  getReportingType,
  getSelectedCampaigns,
  getStaticMode
} from '@/store/selectors/reporting.selectors';
import { getCampaigns } from '@/store/selectors/reportingAdvancedFilter.selectors';
import { getUserRoles } from '@/store/selectors/resources.selectors';
import { getCurrentUser } from '@/store/selectors/user.selectors';
import { Legends } from '@/ts-modules/reporting/Map/Legends';
import { MapInnerControls } from '@/ts-modules/reporting/Map/MapControls/MapInnerControls';
import { MapPopupContent } from '@/ts-modules/reporting/Map/MapPopupContent';
import { RankingRightPanel } from '@/ts-modules/reporting/Map/RankingRightPanel';
import { getCartographyDictionary } from '@/ts-resources/reporting.resources';
import { LegendsScroll, MapViewStyled } from './MapView.styled';

import type { GeoDictionaryTagsConfig } from 'mercator-cartography-api-client/lib/interfaces/geoDictionary.types';

const INTERACTIVE_LAYER_PATTERNS = [
  'campaignItems_stores_outer_border',
  'campaignLocations',
  'locations_multi_country_level'
];

type Props = {
  filtersComponent: React.ReactNode;
  isFootfallAttrMap: boolean;
  layers: Record<string, any>[];
  mapId: string;
  multipleMap?: boolean;
};

type State = {
  countriesLevels: Record<string, any>;
  defaultLevel: string;
};

function MapView({ filtersComponent, isFootfallAttrMap, layers, mapId, multipleMap = false }: Props) {
  const dispatch = useDispatch();
  const legendsRef = useRef<HTMLDivElement>(null);
  const tokens: Record<string, any> = {};

  const defaultBounds = useSelector(getDefaultMapBoundingBox);
  const staticMode = useSelector(getStaticMode);
  // @ts-ignore
  const mapLoading = useSelector(store => getMapLoading(store, mapId));
  const user = useSelector(getCurrentUser);
  const userRoles = useSelector(getUserRoles);
  const selectedCampaignIds = useSelector(getCampaigns);
  const reportingCampaigns: Record<string, any>[] = useSelector(getSelectedCampaigns);
  const reportingType = useSelector(getReportingType);
  const mapEvents = useSelector(getMapEvents);
  const metricsAndKpi = useSelector(getMetricsAndKpisByType);
  const countries: string[] = useSelector(getCountriesWithEvents);

  const metricsAndKpiFiltered = metricsAndKpi
    .filter(item => !STORE_VISITS.includes(item))
    .map(item => getMetricsKeys(item));

  const mapContext = useReportingMap();
  const {
    countryLevelWithEvents,
    getMap,
    setMap,
    setMapCartographySettings,
    setMapLegend,
    setMapSettings,
    state: { maps }
  } = mapContext;

  const map = getMap({ id: mapId });
  const isCartographyMap = map?.type === 'cartography';
  const cartographySettings = {
    ...map?.settings.cartography,
    status: map?.settings.cartography.status || 'idle'
  };
  const mapStyle = map?.mode === 'light' ? 'light' : 'dark';
  const prevSettingsEvents = usePrevious(map?.settings[map?.type].events);
  const prevMapType = usePrevious(map?.type);
  const metric = map?.settings[map?.type].events[0];
  const campaigns = reportingCampaigns.filter(reportingCampaign => selectedCampaignIds.includes(reportingCampaign.id));
  const cids = campaigns.map(campaign => campaign.id);
  const prevCids = usePrevious(cids);
  const hasCartographyPermissions = checkUserResourcePermissions('reportingCartographyMap', user, userRoles);
  const rankingPermissions = checkUserResourcePermissions('reportingMapRanking', user, userRoles);

  const [{ countriesLevels, defaultLevel }, setState] = useState<State>({
    countriesLevels: {},
    defaultLevel: countries.length > 1 ? 'level0' : countryLevelWithEvents || 'level0'
  });

  const defaultCountries =
    Object.keys(countriesLevels) && Object.keys(countriesLevels).length ? Object.keys(countriesLevels) : countries;

  const prevDefaultLevel = usePrevious(defaultLevel);

  const mapControls = useMemo(() => {
    return [
      {
        Component: MapInnerControls,
        componentProps: {
          campaignsCountries: countries,
          className: 'mapboxgl-ctrl innerComponents',
          isFootfallAttrMap,
          mapId: map?.id,
          supportedCartographyCountries: Object.entries(countriesLevels).reduce(
            (acc: string[], [countryCode, countryLevels]) =>
              countryLevels.length > 0 ? acc.concat(countryCode.toLowerCase()) : acc,
            []
          )
        },
        id: `${mapId}_innerControls`,
        position: 'top-left'
      }
    ];
  }, [countries, countriesLevels]);

  const includeMapRanking = rankingPermissions && maps?.length === 1;

  const eventsByType = isCartographyMap ? metricsAndKpiFiltered : mapEvents;

  const setMapTypeSettings = () => {
    if (!isFootfallAttrMap) {
      let defaultEvents = eventsByType;
      if (campaigns.filter(campaign => isDOOHCampaign(campaign.type)).length > 0) {
        defaultEvents = [...eventsByType.slice(0, 1), 'playouts', ...eventsByType.slice(1)];
      }
      if (map) {
        setMapSettings({
          id: mapId,
          value: {
            ...map.settings,
            cartography: {
              ...map.settings.cartography,
              events: defaultEvents.slice(0, 1),
              level: defaultLevel
            },
            heatmap: {
              ...map.settings.heatmap,
              events: defaultEvents.slice(0, 1)
            },
            single: {
              ...map.settings.single,
              events: defaultEvents
            }
          }
        });
      }
    }
  };

  useEffect(() => {
    if (!isEqual(cids, prevCids) || !isEqual(prevMapType, map?.type)) {
      setMapTypeSettings();
    }
  });

  useEffect(() => {
    if (map?.type === 'cartography' && !prevDefaultLevel && defaultLevel) {
      setMapCartographySettings({
        id: mapId,
        value: {
          ...map.settings[map.type],
          countries: map.settings[map.type].countries.length ? map.settings[map.type].countries : defaultCountries,
          events: map.settings[map.type].events.slice(0, 1),
          level: defaultLevel
        }
      });
      dispatch(
        reportingSetCartographyMapConfig({
          countries: map.settings[map.type].countries,
          level: defaultLevel,
          metric: map.settings[map.type].events.slice(0, 1)[0]
        })
      );
    }
  });

  const [legendsScroll, setLegendsScroll] = useState(false);

  const legendsHasScroll = legendsRef.current ? legendsRef.current.scrollWidth > legendsRef.current.clientWidth : false;

  useLayoutEffect(() => {
    setLegendsScroll(legendsHasScroll);
  }, [legendsHasScroll]);

  let newCountries = countries;
  if (isCartographyMap) {
    const cartographicCountries = (map?.settings[map?.type] as CartographySettings).countries;
    if (cartographicCountries) {
      newCountries = cartographicCountries;
    }
  }

  useEffect(() => {
    if (!isEqual(prevMapType, map?.type) || !isEqual(prevSettingsEvents, map?.settings[map?.type].events)) {
      setMapLegend({
        id: mapId,
        value: map?.settings[map?.type].events.map((event: string) => {
          const existingEventInLegend = map?.legend.find((item: Legend) => item.event === event);
          return {
            event,
            visible: existingEventInLegend?.visible || true
          };
        }) as Legend[]
      });
    }
  });

  const fetchCountriesLevels = async () => {
    try {
      const countriesInfo = await getCartographyDictionary(countries, tokens.countriesLevels);
      const newOptions = countriesInfo.reduce(
        (prev, curr) => ({
          ...prev,
          [curr.id!]:
            curr.tagsConfig
              ?.filter(tag => tag?.reportingMapEventsEnable)
              .map(option => ({
                id: option?.id || 'N/A',
                label: option?.name || 'N/A'
              })) || []
        }),
        {} as Record<string, GeoDictionaryTagsConfig[]>
      );

      if (map?.type === 'cartography' && isEmpty(newOptions)) {
        setMap({
          id: mapId,
          value: {
            ...map,
            type: 'single'
          }
        });
      } else if (map?.type === 'cartography') {
        const selectedCountries = map.settings[map.type].countries.length
          ? map.settings[map.type].countries
          : countries;
        const selectedLevel = map.settings[map.type].level;
        if (selectedCountries.length > 1) {
          setState(prevState => ({
            ...prevState,
            defaultLevel: 'level0'
          }));
        } else {
          const selectedCountry = selectedCountries[0];
          const countryOptions = selectedCountry ? newOptions[selectedCountry] : Object.values(newOptions)[0];
          const invalidLevelId = !countryOptions?.find(({ id: levelId }) => levelId === selectedLevel);
          if (!selectedCountry || invalidLevelId) {
            const newDefaultCountry = Object.keys(newOptions)[0];
            const newDefaultLevel = newOptions[selectedCountry || newDefaultCountry][0].id!;
            setState(prevState => ({
              ...prevState,
              defaultLevel: newDefaultLevel
            }));
          }
        }
      }

      setState(prevState => ({
        ...prevState,
        countriesLevels: newOptions
      }));
    } catch (e: any) {
      // biome-ignore lint/suspicious/noConsole: off
      console.info(e.message);
    }
  };

  useEffect(() => {
    if (map?.type === 'cartography' && (!hasCartographyPermissions || staticMode)) {
      setMap({
        id: mapId,
        value: {
          ...map,
          type: 'single'
        }
      });
    }
    fetchCountriesLevels();
    const includeScreens = reportingType && !isDigitalCampaign(reportingType);
    if (includeScreens) {
      dispatch(reportingFetchCampaignScreens(cids));
    }
    return () =>
      Object.values(tokens).forEach(token => {
        token.cancel?.();
      });
  }, []);

  const itemLabelCartographyHeatmap = (() => {
    return (
      countriesLevels[newCountries[0]?.toUpperCase()]?.find(
        (countryLevel: Record<string, any>) => countryLevel.id === cartographySettings.level
      )?.label || cartographySettings.level
    );
  })();

  if (!user || !userRoles || userRoles.length === 0 || !defaultBounds) {
    return null;
  }

  const getPopupConfig = (): Record<string, any> => {
    switch (map?.type) {
      case 'cartography':
        return cartographySettings.level
          ? {
              popupConfig: {
                Component: MapPopupContent,
                componentProps: {
                  className: 'reportingMap__popupContainer',
                  itemLabel: itemLabelCartographyHeatmap,
                  mapId,
                  metricLabel: map?.settings[map?.type].events[0]
                },
                layerIds:
                  cartographySettings?.countries?.map(country => `layer_${mapId}_cartographyHeatmap_${country}`) || []
              }
            }
          : {};
      case 'screens':
        return {
          popupConfig: {
            Component: MapPopupContent,
            componentProps: {
              className: 'reportingMap__popupContainer',
              itemLabel: 'Screen',
              mapId,
              metricLabel: map?.settings[map?.type].events[0]
            },
            layerIds: [`${mapId}_campaignItems_screens_outer_border`]
          }
        };
      case 'locations':
      case 'stores':
        return {
          popupConfig: {
            Component: MapPopupContent,
            componentProps: {
              className: 'reportingMap__popupContainer',
              mapId,
              metricLabel: map?.settings[map?.type].events[0]
            }
          }
        };
      default:
        return {};
    }
  };

  return (
    <MapViewStyled className="mapView" multipleMap={multipleMap}>
      {filtersComponent}
      <div className="mapView__container">
        <div className="mapView__leftContainer">
          <div className="mapView__map">
            {mapLoading && (
              <div className="spinnerBg">
                <Loader showBackground type="landing" variant="default" />
              </div>
            )}
            <MapComponent
              additionControls={mapControls}
              {...(defaultBounds && { defaultBounds })}
              fitBoundsOptions={{
                linear: true,
                padding: 50
              }}
              id={mapId}
              interactiveLayerPatterns={INTERACTIVE_LAYER_PATTERNS}
              layers={layers}
              mapStyle={mapStyle}
              maxZoom={isCartographyMap ? 13 : 18}
              movingMethod="easeTo"
              navigationControl
              preserveDrawingBuffer
              {...getPopupConfig()}
            />
          </div>
          <CustomThemeProvider themeContrast="dark">
            <div className="mapView__legends">
              <LegendsScroll className="mapView__legends-container" hasScroll={legendsScroll} ref={legendsRef}>
                <Legends mapId={mapId} metricLabel={metric || 'N/A'} />
              </LegendsScroll>
            </div>
          </CustomThemeProvider>
        </div>
        {includeMapRanking && (
          <RankingRightPanel className="mapView__ranking" isFootfallAttrMap={isFootfallAttrMap} mapId={mapId} />
        )}
      </div>
    </MapViewStyled>
  );
}

export default MapView;
